import posthog from 'posthog-js';

// PostHog (EU Cloud) browser SDK wrapper. Mirrors the `readAuth0Config`
// missing-config guard in main.tsx: a blank `VITE_POSTHOG_KEY` makes the
// whole module a no-op so local dev / preview deploys never pollute the
// prod dataset (see AGENTS.md → Analytics). The Auth0 `sub` is the
// canonical `distinct_id` so client + server events stitch together.

// Default host kept in sync with AGENTS.md / .env.example. Only override
// VITE_POSTHOG_HOST to point at a reverse proxy.
const DEFAULT_POSTHOG_HOST = 'https://eu.i.posthog.com';

export interface PostHogConfig {
  readonly key: string;
  readonly host: string;
}

export function readPostHogConfig(): PostHogConfig | null {
  const key = import.meta.env.VITE_POSTHOG_KEY?.trim();
  if (!key) return null;
  // A blank/whitespace host falls back to the EU default (so `??` won't
  // do — it would keep an empty string); only override it to point at a
  // reverse proxy.
  const host = import.meta.env.VITE_POSTHOG_HOST?.trim();
  if (host) return { key, host };
  return { key, host: DEFAULT_POSTHOG_HOST };
}

// Module-level flag so identify/reset are no-ops until a real key has
// initialized the SDK. Guards double-init under React StrictMode too.
let enabled = false;

export function initPostHog(config: PostHogConfig | null): boolean {
  if (enabled) return true;
  if (!config) return false;
  posthog.init(config.key, {
    api_host: config.host,
    // Only create person profiles for identified users — anonymous
    // pageviews stay event-only. Matches the issue spec.
    person_profiles: 'identified_only',
    // Session replay is ON (issue #252) but ships with a masking baseline
    // so it never captures PII, per AGENTS.md → Analytics ("must not ship
    // a default-on recorder that captures keystrokes"):
    //   - `maskAllInputs` masks every text field's value (profile edit,
    //     add-user email, search, rating notes, login is on a separate
    //     Auth0 origin so never reaches us).
    //   - `maskTextSelector` masks the displayed text of any element
    //     tagged `data-ph-mask` (account email/name, the Beta Users admin
    //     list). Other users' display names stay visible so the replay is
    //     still useful for observing UX.
    // 100% sample rate is fine for the invite-only beta (well within the
    // free tier); revisit before any wider rollout.
    disable_session_recording: false,
    session_recording: {
      maskAllInputs: true,
      maskTextSelector: '[data-ph-mask]',
    },
    // Autocapture is off: it records clicked elements' textContent (e.g.
    // rating notes) and the event taxonomy is a separate issue.
    // `mask_personal_data_properties` masks PII in any captured event
    // properties as defense in depth.
    autocapture: false,
    mask_personal_data_properties: true,
    // SPA pageviews are fired manually on every React Router navigation
    // (see analytics/RouteAnalytics.tsx) with a normalized route +
    // page_name, so the SDK's own auto pageview would double-count the
    // first load — turn it off. `$pageleave` (time-on-page) stays on so
    // the manual pageviews still get a matching leave event.
    capture_pageview: false,
    capture_pageleave: true,
  });
  enabled = true;
  return true;
}

export function isPostHogEnabled(): boolean {
  return enabled;
}

export function identifyUser(
  sub: string,
  properties: { email?: string; name?: string },
): void {
  if (!enabled) return;
  posthog.identify(sub, properties);
}

export function resetPostHog(): void {
  if (!enabled) return;
  posthog.reset();
}

// Low-level guarded event capture. No-op until a real key has initialized
// the SDK (same posture as identify/reset), so callers never need to guard
// on `isPostHogEnabled()` themselves. The typed product-event taxonomy in
// `analytics/events.ts` builds on top of this — prefer those wrappers over
// calling `capture` with a raw string.
export function capture(
  event: string,
  properties?: Record<string, unknown>,
): void {
  if (!enabled) return;
  posthog.capture(event, properties);
}

// Test-only seam: the `enabled` flag is write-once at runtime (init is
// idempotent by design), so tests reset it to assert the unconfigured
// no-op path deterministically. Guarded on `import.meta.env.PROD` (Vite
// replaces it statically) so an accidental call from app code can never
// silently disable analytics in a production build. Not used by app code.
export function __resetPostHogForTests(): void {
  if (import.meta.env.PROD) return;
  enabled = false;
}

export { posthog };
